import { Form, Spin, type FormInstance } from "antd";
import {
  FileText,
  ImagePlus,
  RefreshCw,
  Trash2,
  UploadCloud,
} from "lucide-react";
import { useRef, useState, type DragEvent } from "react";
import cn from "../../../lib/cn";
import { getFileNameFromUrl } from "../../../utils/upload";
import useFileUpload from "./useFileUpload";

/* ----------------------------------------------------------------------------
 * Upload fields for the Apply page. Same upload API and value shapes as the
 * shared PhotoUploader / FileUploader:
 *   photo   → "https://…/file.jpg"
 *   cv_link → ["https://…/resume.pdf"]
 * -------------------------------------------------------------------------- */

const FieldLabel = ({ children }: { children: string }) => (
  <p className="mb-2 text-sm font-medium text-gray-700">
    {children}
    <span className="ml-1 text-rose-500">*</span>
  </p>
);

/* ================================ Photo ================================= */

export const PhotoField = ({ form }: { form: FormInstance }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { upload, uploading } = useFileUpload();
  const photo: string | undefined = Form.useWatch("photo", form);

  const handleFile = async (file?: File) => {
    if (!file) return;
    const url = await upload(file);
    if (url) form.setFieldValue("photo", url);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div>
      <FieldLabel>Profile photo</FieldLabel>
      <div className="flex items-center gap-4 rounded-xl border border-gray-200 p-3">
        <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-full bg-gray-100 ring-1 ring-gray-200">
          {photo ? (
            <img
              src={photo}
              alt="Profile"
              className="h-full w-full object-cover"
            />
          ) : (
            <span className="flex h-full w-full items-center justify-center text-gray-400">
              <ImagePlus className="h-6 w-6" strokeWidth={1.5} />
            </span>
          )}
          {uploading && (
            <span className="absolute inset-0 flex items-center justify-center bg-white/80">
              <Spin size="small" />
            </span>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={uploading}
              onClick={() => inputRef.current?.click()}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-[13px] font-medium text-gray-700 transition-colors hover:border-gray-300 hover:text-gray-900 disabled:opacity-60"
            >
              {photo ? (
                <RefreshCw className="h-3.5 w-3.5" />
              ) : (
                <UploadCloud className="h-3.5 w-3.5" />
              )}
              {uploading ? "Uploading…" : photo ? "Change" : "Upload photo"}
            </button>
            {photo && !uploading && (
              <button
                type="button"
                onClick={() => form.setFieldValue("photo", null)}
                aria-label="Remove photo"
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-rose-50 hover:text-rose-500"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
          <p className="mt-1.5 text-xs text-gray-400">PNG or JPG, up to 5 MB</p>
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept=".png,.jpg,.jpeg"
        className="!hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
    </div>
  );
};

/* ================================ Resume ================================ */

export const ResumeField = ({ form }: { form: FormInstance }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { upload, uploading } = useFileUpload();
  const [dragging, setDragging] = useState(false);
  const cvLinks: (string | undefined)[] | undefined = Form.useWatch(
    "cv_link",
    form,
  );
  const resume = cvLinks?.[0];

  const handleFile = async (file?: File) => {
    if (!file) return;
    const url = await upload(file);
    if (url) form.setFieldValue("cv_link", [url]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  return (
    <div>
      <FieldLabel>Resume / CV</FieldLabel>
      {resume ? (
        <div className="flex items-center gap-3 rounded-xl border border-gray-200 p-3">
          <span className="flex h-16 w-12 shrink-0 items-center justify-center rounded-lg bg-theme-primary/10 text-theme-primary">
            <FileText className="h-5 w-5" strokeWidth={1.75} />
          </span>
          <div className="min-w-0 flex-1">
            <a
              href={resume}
              target="_blank"
              rel="noreferrer"
              className="block truncate text-sm font-medium text-gray-900 hover:text-theme-primary"
            >
              {getFileNameFromUrl(resume)}
            </a>
            <div className="mt-1.5 flex gap-3 text-[13px]">
              <button
                type="button"
                disabled={uploading}
                onClick={() => inputRef.current?.click()}
                className="font-medium text-theme-primary hover:underline disabled:opacity-60"
              >
                {uploading ? "Uploading…" : "Replace"}
              </button>
              {!uploading && (
                <button
                  type="button"
                  onClick={() => form.setFieldValue("cv_link", [])}
                  className="font-medium text-gray-500 hover:text-rose-500"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={cn(
            "relative flex w-full items-center gap-3 rounded-xl border border-dashed p-3 text-left transition-colors",
            dragging
              ? "border-theme-primary bg-theme-primary/[0.04]"
              : "border-gray-300 hover:border-theme-primary/60 hover:bg-gray-50/70",
          )}
        >
          <span className="flex h-16 w-12 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-400">
            {uploading ? (
              <Spin size="small" />
            ) : (
              <UploadCloud className="h-5 w-5" strokeWidth={1.75} />
            )}
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-medium text-gray-900">
              {uploading ? (
                "Uploading…"
              ) : (
                <>
                  <span className="text-theme-primary">Click to upload</span> or
                  drag & drop
                </>
              )}
            </span>
            <span className="mt-1 block text-xs text-gray-400">
              PDF, DOC or DOCX, up to 5 MB
            </span>
          </span>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.doc,.docx"
        className="!hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
    </div>
  );
};
